import { Link } from "react-router-dom";

import { StatusBadge } from "../components/Badges";
import { BarList } from "../components/BarList";
import { IssueList } from "../components/IssueList";
import { MonthBars } from "../components/MonthBars";
import { Card, ErrorBox, Loading, PageHeader } from "../components/Page";
import { StatTile } from "../components/StatTile";
import type { DocSummary, Issue } from "../lib/api";
import { day, num, pct, usd } from "../lib/format";
import { useApi } from "../lib/useApi";

interface Overview {
  sales: { amount_usd: number; cases: number; as_of: string | null; reconciles: boolean | null;
           by_channel: { code: string; name: string; amount_usd: number; cases: number }[] };
  us_detail: { amount_usd: number; cases: number; lines: number; as_of: string | null };
  stock: { finished_cases: number; locations: number };
  in_transit: { cases: number; documents: number };
  documents: Record<string, number>;
  issues: { error: number; warning: number; info: number; total: number };
  unmatched: number;
}
interface Monthly { months: { month: string; amount_usd: number; cases: number }[]; undated: { count: number; amount_usd: number } }
interface Cust { id: number; name: string; channel_name: string; amount_usd: number; share: number }

export function Overview() {
  const o = useApi<Overview>("/overview");
  const monthly = useApi<Monthly>("/sales/monthly");
  const customers = useApi<{ customers: Cust[] }>("/sales/by-customer?limit=8");
  const issues = useApi<Issue[]>("/issues?severity=error");
  const docs = useApi<DocSummary[]>("/documents");

  if (o.error) return <ErrorBox error={o.error} onRetry={o.reload} />;
  if (!o.data) return <Loading />;
  const d = o.data;
  const top = customers.data?.customers[0];
  // The chart is titled 2026, so it shows 2026. Rows dated outside it (three
  // typos in the sales sheet) are on the Data quality page, not silently here.
  const months = (monthly.data?.months ?? []).filter((m) => m.month >= "2026-01" && m.month <= "2026-12");

  return (
    <>
      <PageHeader title="Overview" sub={`All channels, year to date · sales report as of ${day(d.sales.as_of)}`} />

      <section className="tiles" aria-label="Headline figures">
        <StatTile label="Sales YTD" value={usd(d.sales.amount_usd, true)} sub={`${num(d.sales.cases)} cases, all channels`}
                  to="/sales" status={d.sales.reconciles === false ? "critical" : undefined} />
        <StatTile label="Finished stock" value={num(d.stock.finished_cases)} sub={`cases across ${d.stock.locations} locations`} to="/stock" />
        <StatTile label="In transit" value={num(d.in_transit.cases)} sub={`cases on ${d.in_transit.documents} transfer(s)`} to="/stock?tab=transit" />
        <StatTile label="Largest customer" value={pct(top?.share)} sub={top?.name ?? "—"} to={top ? `/customers/${top.id}` : undefined}
                  status={top && top.share > 0.3 ? "warning" : undefined} />
        <StatTile label="Open data issues" value={num(d.issues.total)} sub={`${d.issues.error} errors · ${d.issues.warning} warnings`}
                  to="/data-quality" status={d.issues.error ? "critical" : d.issues.total ? "warning" : "good"} />
        <StatTile label="Draft documents" value={num(d.documents.draft ?? 0)} sub={`${num(d.documents.issued ?? 0)} issued, not yet shipped`} to="/documents?status=draft" />
      </section>

      <div className="two-up">
        <Card title="Sales by channel" actions={<Link to="/sales" className="card-link">All sales →</Link>}>
          <BarList bars={d.sales.by_channel.map((c) => ({
            key: c.code, label: c.name, value: c.amount_usd, valueLabel: usd(c.amount_usd), meta: `${num(c.cases)} cs`,
            href: `/sales?tab=invoices&channel=${c.code}`,
          }))} />
        </Card>
        <Card title="Revenue by month, 2026">
          {monthly.data ? (
            <>
              <MonthBars title="Revenue" points={months.map((m) => ({ month: m.month, value: m.amount_usd, label: usd(m.amount_usd) }))} />
              {monthly.data.undated.count > 0 && (
                <p className="note muted">{monthly.data.undated.count} undated invoices ({usd(monthly.data.undated.amount_usd)}) are not in any month.</p>
              )}
            </>
          ) : <Loading />}
        </Card>
      </div>

      <div className="two-up">
        <Card title="Top customers" actions={<Link to="/customers" className="card-link">All customers →</Link>}>
          {customers.data ? (
            <BarList bars={customers.data.customers.map((c) => ({
              key: String(c.id), label: c.name, value: c.amount_usd, valueLabel: usd(c.amount_usd), meta: pct(c.share),
              href: `/customers/${c.id}`,
            }))} />
          ) : <Loading />}
        </Card>
        <Card title="Errors to look at" actions={<Link to="/data-quality" className="card-link">All issues →</Link>}>
          {issues.data ? <IssueList issues={issues.data.slice(0, 6)} compact /> : <Loading />}
        </Card>
      </div>

      <Card title="Recent documents" actions={<Link to="/documents" className="card-link">All documents →</Link>}>
        {docs.data ? (
          <ul className="doc-list">
            {docs.data.slice(0, 6).map((doc) => (
              <li key={doc.id}>
                <Link to={`/documents/${doc.id}`}>{doc.type_label} {doc.number}</Link>
                <span className="muted">{doc.counterparty}</span>
                <StatusBadge status={doc.status} docType={doc.doc_type} />
                <span className="n">{usd(doc.total_amount)}</span>
              </li>
            ))}
          </ul>
        ) : <Loading />}
      </Card>
    </>
  );
}
